import { QueryFunction } from '@tanstack/react-query';
import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';
import type { ResidualFormConfig } from 'src/services/incomeTypes/formJson.types';

/** Cells of one panel table: `table -> row -> column`. */
export type ResidualTableValues = Record<
  string,
  Record<string, Record<string, unknown>>
>;

/**
 * One loan's Residual Income worksheet: the tenant's configuration and this
 * loan's values, together -- the screen cannot render either alone.
 */
export interface LoanResidualIncome {
  incomeTypeId: string | null;
  version: number | null;
  /** Null on a tenant the seed has not reached. */
  config: ResidualFormConfig | null;
  values: Record<string, unknown>;
  tables: ResidualTableValues;
  /** `table.row.column` keys the server owns; the rest are the reviewer's. */
  computedKeys: string[];
  updatedAt: string | null;
}

const EMPTY: LoanResidualIncome = {
  incomeTypeId: null,
  version: null,
  config: null,
  values: {},
  tables: {},
  computedKeys: [],
  updatedAt: null,
};

const getLoanResidualIncome: QueryFunction<
  LoanResidualIncome,
  ['/loans/residual-income', string]
> = async ({ queryKey }) => {
  const [, loanId] = queryKey;
  if (isMockServices()) return await mock(EMPTY);

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<{
    success: boolean;
    data: LoanResidualIncome;
  }>(`/loans/${loanId}/residual-income`, { method: 'GET' });

  return response.data?.data ?? EMPTY;
};

export default getLoanResidualIncome;
